import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  Inventory2,
  Archive,
  AddCircleOutline,
  WarningAmber,
  Category,
  Build,
  Business
} from '@mui/icons-material';
import axios from 'axios';

const SummaryCard = ({ title, value, subtext, icon: Icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', borderRadius: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
          {subtext != null && (
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            p: 1.5,
            borderRadius: 2
          }}
        >
          {Icon && <Icon sx={{ fontSize: 28 }} />}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const BarList = ({ items, maxCount, labelKey = 'name', valueKey = 'count', emptyMessage }) => {
  const values = (items || []).map((i) => i[valueKey] || 0);
  const max = maxCount ?? (values.length ? Math.max(...values, 1) : 1);
  return (
    <List dense disablePadding>
      {!items?.length ? (
        <ListItem>
          <ListItemText primary={emptyMessage || 'No data'} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
        </ListItem>
      ) : (
        items.map((item, idx) => (
          <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
            <ListItemText
              primary={item[labelKey] || '—'}
              secondary={
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, ((item[valueKey] || 0) / max) * 100)}
                  sx={{ mt: 0.5, height: 6, borderRadius: 1, bgcolor: 'action.hover' }}
                  color="primary"
                />
              }
              primaryTypographyProps={{ variant: 'body2' }}
            />
            <Chip size="small" label={item[valueKey]} sx={{ ml: 1 }} />
          </ListItem>
        ))
      )}
    </List>
  );
};

/**
 * Parts Catalog Dashboard – summary view with counts and breakdowns
 */
const PartsCatalogDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get('/api/parts/summary');
        if (!cancelled) setSummary(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load summary');
          setSummary(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, []);

  if (loading && !summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={220} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const data = summary || {};
  const totalActive = data.totalActive ?? 0;
  const totalArchived = data.totalArchived ?? 0;
  const recentCount = data.recentCount ?? 0;
  const reorderRequiredCount = data.reorderRequiredCount ?? 0;
  const byCategory = data.byCategory || [];
  const byPartType = data.byPartType || [];
  const byManufacturer = data.byManufacturer || [];
  const maxCategory = Math.max(...byCategory.map((c) => c.count || 0), 1);
  const maxPartType = Math.max(...byPartType.map((p) => p.count || 0), 1);
  const maxManufacturer = Math.max(...byManufacturer.map((m) => m.count || 0), 1);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom color="text.secondary">
        Overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Active parts"
            value={totalActive}
            subtext="In catalog"
            icon={Inventory2}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Archived"
            value={totalArchived}
            subtext="Inactive parts"
            icon={Archive}
            color="grey"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Added (30 days)"
            value={recentCount}
            subtext="Recently added"
            icon={AddCircleOutline}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Reorder required"
            value={reorderRequiredCount}
            subtext="Parts with reorder level"
            icon={WarningAmber}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Category fontSize="small" /> By category
              </Typography>
              <BarList items={byCategory} maxCount={maxCategory} emptyMessage="No categories" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build fontSize="small" /> By part type
              </Typography>
              <BarList items={byPartType} maxCount={maxPartType} emptyMessage="No part types" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business fontSize="small" /> Top manufacturers
              </Typography>
              <BarList items={byManufacturer} maxCount={maxManufacturer} emptyMessage="No manufacturers" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartsCatalogDashboard;
